/**
 * カスタムベクター hooks (Phase 30)
 * - useCustomVectors: 一覧取得
 * - useUploadCustomVector: Storage へ .svg をアップロード + Firestore へ登録
 * - useDeleteCustomVector: Firestore + Storage から削除
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';

import { db, storage } from '@/lib/firebase';
import { safeAddDoc } from '@/lib/safeWrite';

import type { CustomVector, CustomVectorCategory } from '@goenhub/shared';
import {
  CUSTOM_VECTOR_MAX_BYTES,
  CUSTOM_VECTOR_STORAGE_PATH,
} from '@goenhub/shared';

export type CustomVectorRecord = CustomVector;

export function useCustomVectors() {
  return useQuery({
    queryKey: ['customVectors'],
    queryFn: async (): Promise<CustomVectorRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'customVectors'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => {
        const data = d.data() as CustomVector;
        return { ...data, id: d.id };
      });
    },
  });
}

export function useUploadCustomVector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      file: File;
      name: string;
      category: CustomVectorCategory;
    }) => {
      const { file, name, category } = params;
      if (file.size > CUSTOM_VECTOR_MAX_BYTES) {
        throw new Error(`ファイルサイズが大きすぎます（最大 ${CUSTOM_VECTOR_MAX_BYTES / 1024 / 1024}MB）`);
      }
      if (!file.name.toLowerCase().endsWith('.svg')) {
        throw new Error('SVG ファイル (.svg) のみ対応しています');
      }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${CUSTOM_VECTOR_STORAGE_PATH}/${Date.now()}-${safeName}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: 'image/svg+xml' });
      const url = await getDownloadURL(ref);
      await safeAddDoc(collection(db, 'customVectors'), {
        name,
        url,
        storagePath: path,
        category,
        size: file.size,
        createdAt: serverTimestamp(),
      });
      await qc.invalidateQueries({ queryKey: ['customVectors'] });
    },
  });
}

export function useDeleteCustomVector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vector: CustomVectorRecord) => {
      try {
        await deleteObject(storageRef(storage, vector.storagePath));
      } catch (e) {
        console.warn('[useDeleteCustomVector] Storage delete failed (continue):', e);
      }
      await deleteDoc(doc(db, 'customVectors', vector.id));
      await qc.invalidateQueries({ queryKey: ['customVectors'] });
    },
  });
}
